// Shared Won / Lost confirmation for an opportunity — used by the
// opportunity DETAIL page ("Mark Won" / "Mark Lost" buttons) and the
// opportunity BOARD (dragging a card onto the Won / Lost drop zones), so
// the two surfaces can never drift apart on copy, validation, or
// behavior.
//
// The modal owns the whole flow: the plain-English consequence copy, the
// required lost reason (Category 3 in-flow capture — the lost-reason
// breakdown metric depends on it), a per-company "which product did they
// buy?" pick where the win is otherwise ambiguous, the API calls, and
// error toasts. SUCCESS is handed back to the caller (onWon / onLost)
// because the right next step differs by surface — the detail page
// navigates to the new Processing record, the board removes the card
// and offers a link instead.

import { useEffect, useState } from "react";

import { LOST_REASON_OPTIONS } from "@shared/opportunities/lost-reasons";
import {
  INSURANCE_POLICY_SERVICE_TYPES,
  PLANNING_SERVICE_TYPES,
  insuranceWonPolicyTypePrefill,
  planningWonCaseTypePrefill,
} from "@shared/db/service-type-catalog";
import { humanizeServiceType } from "@shared/db/service-type-labels";
import {
  GROUP_PLAN_LINE_BY_TRANSACTION_TYPE,
  productStageLabel,
} from "@shared/service-records/product-stage";
import {
  deriveOpportunityServiceDefaults,
  deriveServiceShapeForType,
} from "@shared/opportunities/opportunity-service-map";

import { arizonaToday } from "@shared/processing/key-dates";

import { api, ApiError, type OfferTermOnCardDto, type UncarriedOfferTermDto } from "../api";
import { DateField } from "./DateField";
import { Button, Modal } from "../ui";
import { useToast } from "./Toast";

/** The slice of an opportunity both consumers can supply (the detail
 *  page has the full record, the board only its list item). */
export interface OpportunityOutcomeTarget {
  id: string;
  household_display_name: string;
  company_slug: string;
  opportunity_type: string;
}

export interface OpportunityWonResult {
  opportunity_id: string;
  service_record_id: string;
  transaction_id: string;
  /** Set when the win came from accepting a Realty offer. */
  accepted_offer_id?: string | null;
  carried_terms?: Record<string, string | number> | null;
  /** Every term that could NOT reach the contract card, each with a
   *  sentence saying why. The advisor accepted that offer believing the
   *  OS had it, so this is never silent. */
  uncarried_terms?: UncarriedOfferTermDto[];
  /** Shown on the contract card straight from the accepted offer. */
  from_offer_terms?: OfferTermOnCardDto[];
}

/**
 * The Realty offer a win is accepting, when it came from one.
 *
 * Named rather than passed as a bare id so the dialog can say WHICH offer
 * it is about. "Mark this won?" and "Accept Kowalski's offer at $385,000?"
 * are different questions, and on a listing with four offers on the table
 * the second is the only one worth answering.
 */
export interface AcceptedOfferContext {
  id: string;
  label: string;
  /** As accepted — the string that prints on the contract. */
  price: string | null;
}

// What the product is CALLED at each company, so the sentence reads the
// way the team talks (vocabulary lock: product-led, workflow-supported).
function productNounFor(companySlug: string): string {
  switch (companySlug) {
    case "insurance":
      return "policy";
    case "mortgage":
      return "loan";
    case "financial":
      return "managed portfolio";
    case "planning":
      return "case";
    default:
      return "product record";
  }
}

/** A company whose Mark Won dialog must ask "which product did they buy?"
 *  before Processing can show anything more specific than the pipeline's
 *  own name. Only companies whose won-conversion is genuinely ambiguous
 *  need an entry — everyone else derives a real product from the
 *  opportunity type alone (deriveOpportunityServiceDefaults), same as
 *  before this existed. */
interface WonProductPicker {
  /** The question shown above the select, in the company's own words
   *  (vocabulary lock: "case" for Planning, "policy" for Insurance). */
  promptLabel: string;
  pickerLabel: string;
  options: readonly string[];
  /** Non-null return is a PREFILL, not a lock — the picker stays visible
   *  either way so a wrong guess is one click to correct. */
  prefill: (opportunityType: string) => string | null;
}

const WON_PRODUCT_PICKERS: Partial<Record<string, WonProductPicker>> = {
  planning: {
    promptLabel: "Which case did they buy?",
    pickerLabel: "Pick the case type…",
    options: PLANNING_SERVICE_TYPES,
    prefill: planningWonCaseTypePrefill,
  },
  // Brandon Ream, 2026-08-25: quotes accepted on the spot were landing on
  // the Processing board simply labeled "Policy Issuance" — the pipeline
  // name, not the product — because nothing asked which policy it was.
  // Planning had this exact picker already; Insurance has the identical
  // ambiguous shared quote type and never got one.
  insurance: {
    promptLabel: "Which policy did they buy?",
    pickerLabel: "Pick the policy type…",
    options: INSURANCE_POLICY_SERVICE_TYPES,
    prefill: insuranceWonPolicyTypePrefill,
  },
};

export function OpportunityOutcomeModal({
  mode,
  target,
  acceptedOffer,
  onClose,
  onWon,
  onLost,
  beforeConfirm,
}: {
  /** Runs first when the person confirms, before the win or loss is
   *  recorded. Data Needed's stray-win card reopens the stray win here,
   *  so nothing changes until the person has answered every question.
   *  Must be safe to run again: a retry after a failed win runs it twice. */
  beforeConfirm?: () => Promise<void>;
  /** Which confirmation to show; null renders the modal closed. */
  mode: "won" | "lost" | null;
  target: OpportunityOutcomeTarget | null;
  /** The Realty offer being accepted, when this win came from one. */
  acceptedOffer?: AcceptedOfferContext | null;
  /** Called on cancel/dismiss (never mid-submit). */
  onClose: () => void;
  onWon: (result: OpportunityWonResult) => void;
  onLost: (result: { opportunity_id: string }) => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [wonServiceType, setWonServiceType] = useState("");
  // The day the seller accepted, when this win accepts an offer. Day zero
  // for every contract deadline on the new file, so it is asked rather
  // than assumed; today (in Arizona) is the default.
  const [acceptedOn, setAcceptedOn] = useState(() => arizonaToday());

  const open = mode !== null && target !== null;
  // An employer's group sale lands on its own implementation board and
  // delivers a group plan, never a person's case, so it is never asked
  // "which case did they buy?" (the Nov 3 audit's blocker: the question
  // sent a group sale to an individual case board). The server ignores a
  // case type sent for an employer's sale as well.
  const isGroupSale = target
    ? Boolean(
        GROUP_PLAN_LINE_BY_TRANSACTION_TYPE[
          deriveOpportunityServiceDefaults(target.opportunity_type, target.company_slug)
            .transaction_type
        ],
      )
    : false;
  const wonPicker =
    target && !isGroupSale ? WON_PRODUCT_PICKERS[target.company_slug] : undefined;

  // Which Processing board this win will land on — needed to name the
  // stage that creates the product record. Planning is the one company
  // where the answer depends on the case the advisor is picking right now
  // (a life case and an estate plan go to different boards), so it reads
  // the live picker value before falling back to the opportunity's own
  // derived default. Insurance's picker never needs this: every policy
  // kind lands on the same Policy Issuance board regardless of which one
  // is picked, so its transaction_type is already correct from the
  // opportunity type alone.
  const wonTransactionType = target
    ? target.company_slug === "planning" && wonServiceType
      ? (deriveServiceShapeForType("planning", wonServiceType)?.transaction_type ??
        deriveOpportunityServiceDefaults(target.opportunity_type, "planning")
          .transaction_type)
      : deriveOpportunityServiceDefaults(
          target.opportunity_type,
          target.company_slug,
        ).transaction_type
    : "";

  // Fresh form state per opportunity/mode: the lost reason never carries
  // over, and a company with a won-product picker prefills it where the
  // opportunity type implies the answer (each company's generic shared
  // quote type requires a choice instead).
  useEffect(() => {
    if (!open || !target) return;
    setLostReason("");
    setAcceptedOn(arizonaToday());
    setWonServiceType(wonPicker ? (wonPicker.prefill(target.opportunity_type) ?? "") : "");
  }, [open, mode, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- keyed on identity, not the object reference

  async function handleConfirm() {
    if (!open || !target || !mode) return;
    setSubmitting(true);
    try {
      if (beforeConfirm) await beforeConfirm();
      if (mode === "won") {
        const resp = await api.transitionOpportunityToWon(target.id, {
          ...(wonPicker && wonServiceType ? { service_type_override: wonServiceType } : {}),
          // Only present when this win came from accepting an offer. The
          // server marks that offer accepted, points it at the new
          // Processing record, and carries its terms onto the contract
          // card — all inside the transition that already exists, rather
          // than a second copy of it.
          ...(acceptedOffer ? { accepted_offer_id: acceptedOffer.id, accepted_on: acceptedOn } : {}),
        });
        onWon(resp);
      } else {
        const resp = await api.transitionOpportunityToLost(target.id, {
          outcome_reason: lostReason || null,
        });
        onLost(resp);
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't update the opportunity.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!target) return null;

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title={
        mode === "won"
          ? acceptedOffer
            ? "Accept this offer?"
            : "Mark as Won?"
          : "Mark as Lost?"
      }
      size="sm"
      centered
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={mode === "won" ? "primary" : "sw-default"}
            onClick={handleConfirm}
            disabled={
              submitting ||
              (mode === "lost" && !lostReason) ||
              (mode === "won" && !!wonPicker && !wonServiceType) ||
              (mode === "won" && !!acceptedOffer && !acceptedOn)
            }
          >
            {submitting
              ? "Working…"
              : mode === "won"
                ? acceptedOffer
                  ? "Accept → Processing"
                  : "Mark Won → Processing"
                : "Mark Lost"}
          </Button>
        </>
      }
    >
      {mode === "won" ? (
        <div>
          {/* Which offer, in as many words. On a listing with four on the
              table, "Mark this won?" is not a question anyone can answer
              safely — this dialog has to name the one it is about. */}
          {acceptedOffer ? (
            <p>
              Accepting <strong>{acceptedOffer.label}</strong>
              {acceptedOffer.price ? ` at ${acceptedOffer.price}` : ""}. Its terms move onto the
              new Processing record's contract card, and every contract deadline is worked out
              from the day the seller accepted.
            </p>
          ) : null}
          {acceptedOffer ? (
            <label
              style={{
                display: "block",
                fontWeight: 700,
                fontSize: 12,
                marginTop: 12,
                marginBottom: 4,
              }}
            >
              Accepted on
              <DateField
                value={acceptedOn}
                onChange={(v) => setAcceptedOn(v)}
                max={arizonaToday()}
                aria-label="The day the seller accepted"
              />
            </label>
          ) : null}
          <p>
            This closes the opportunity as Won and opens a{" "}
            <strong>Processing</strong> record for{" "}
            {target.household_display_name}. This is reversible from the
            opportunity's history.
          </p>
          {/* Grant, 2026-08-17: winning must not create the policy / loan /
              managed account — the carrier hasn't issued, the loan can still
              be denied, the account holds nothing. Say so here, because the
              old copy promised a product record and people looked for it. */}
          <p className="muted" style={{ fontSize: 13 }}>
            {isGroupSale ? (
              <>
                The implementation file opens on the group board. The employer's group plan is
                created when it reaches <strong>Carrier Issued</strong>.
              </>
            ) : productStageLabel(target.company_slug, wonTransactionType) ? (
              <>
                The {productNounFor(target.company_slug)} itself is created when
                the Processing record reaches{" "}
                <strong>
                  {productStageLabel(target.company_slug, wonTransactionType)}
                </strong>
                . Until then they stay a prospect for this company.
              </>
            ) : (
              <>
                The {productNounFor(target.company_slug)} itself is created when
                the Processing record is completed. Until then they stay a
                prospect for this company.
              </>
            )}
          </p>
          {wonPicker ? (
            // Which product did they buy? Derivation can't answer this
            // for a company's shared generic quote type, so the advisor
            // confirms it here — prefilled where the opportunity type
            // implies it, one extra click at most.
            <label
              style={{
                display: "block",
                fontWeight: 700,
                fontSize: 12,
                marginTop: 12,
                marginBottom: 4,
              }}
            >
              {wonPicker.promptLabel}
              <select
                value={wonServiceType}
                onChange={(e) => setWonServiceType(e.target.value)}
                style={{ display: "block", width: "100%", marginTop: 6 }}
              >
                <option value="">{wonPicker.pickerLabel}</option>
                {wonPicker.options.map((t) => (
                  <option key={t} value={t}>
                    {humanizeServiceType(t)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : (
        <div>
          <p>
            This closes the opportunity as Lost for{" "}
            {target.household_display_name}. No service record or transaction
            is created. This is reversible from the opportunity's history.
          </p>
          <label
            style={{
              display: "block",
              fontWeight: 700,
              fontSize: 12,
              marginBottom: 4,
            }}
          >
            Why was it lost?
            <select
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              style={{ display: "block", width: "100%", marginTop: 6 }}
            >
              <option value="">Pick a reason…</option>
              {LOST_REASON_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </Modal>
  );
}
